package dev.danko.roblox_craft_ai.client

import dev.danko.roblox_craft_ai.uikit.config.BackendEndpoint
import dev.danko.roblox_craft_ai.shared.model.AssetRequest
import dev.danko.roblox_craft_ai.shared.model.AssetResponse
import dev.danko.roblox_craft_ai.shared.model.HealthResponse
import dev.danko.roblox_craft_ai.shared.model.SessionSync
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.client.request.forms.submitFormWithBinaryData
import kotlinx.coroutines.await
import io.ktor.client.statement.*
import dev.danko.roblox_craft_ai.shared.model.AssetReportRequest
import dev.danko.roblox_craft_ai.shared.model.AuthUserDto
import dev.danko.roblox_craft_ai.shared.model.UsageSummaryDto
import dev.danko.roblox_craft_ai.shared.model.BillingCatalogDto
import dev.danko.roblox_craft_ai.shared.model.BillingStatusDto
import dev.danko.roblox_craft_ai.shared.model.MagicLinkRequest
import dev.danko.roblox_craft_ai.shared.model.VerifyMagicLinkRequest
import dev.danko.roblox_craft_ai.shared.model.ErrorResponse
import dev.danko.roblox_craft_ai.shared.model.ReportReason
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException

import dev.danko.roblox_craft_ai.shared.model.ProviderConfigDto
import dev.danko.roblox_craft_ai.shared.model.SaveKeysRequest
import dev.danko.roblox_craft_ai.shared.model.TestKeyRequest
import dev.danko.roblox_craft_ai.shared.model.TestKeyResponse

import dev.danko.roblox_craft_ai.shared.model.AdhocTestKeyRequest
import dev.danko.roblox_craft_ai.shared.model.FullSettingsResponse
import dev.danko.roblox_craft_ai.shared.model.UpdatePreferencesRequest
import dev.danko.roblox_craft_ai.shared.model.ProviderKeyDto
import dev.danko.roblox_craft_ai.shared.model.ProviderMetadataDto
import dev.danko.roblox_craft_ai.shared.model.ReorderKeysRequest
import dev.danko.roblox_craft_ai.shared.model.TestKeyResponseV2
import dev.danko.roblox_craft_ai.shared.model.UpsertProviderKeyRequest
import dev.danko.roblox_craft_ai.shared.model.UserSettingsDtoV2
import dev.danko.roblox_craft_ai.shared.model.ConceptGenerationRequest
import dev.danko.roblox_craft_ai.shared.model.ConceptGenerationResult
import dev.danko.roblox_craft_ai.shared.model.ApproveConceptRequest
import dev.danko.roblox_craft_ai.shared.model.RerollConceptRequest

/** A backend error with its machine-readable code, e.g. CONTENT_BLOCKED or RATE_LIMIT_EXCEEDED. */
class ApiException(val errorCode: String, val serverMessage: String) : Exception("$errorCode: $serverMessage")

object RobloxCraftApiClient {

    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = true
        isLenient = true
    }

    private val client = HttpClient {
        install(ContentNegotiation) {
            json(json)
        }
    }

    private val BASE_URL: String get() = BackendEndpoint.httpBase

    suspend fun getRecentAssets(limit: Int = 50): List<AssetResponse> {
        return try {
            client.get("$BASE_URL/api/v1/assets?limit=$limit").body()
        } catch (_: Throwable) {
            emptyList()
        }
    }

    suspend fun getServerConfig(): dev.danko.roblox_craft_ai.shared.model.ServerConfigDto? {
        return try {
            client.get("$BASE_URL/api/v1/config").body()
        } catch (_: Throwable) {
            null
        }
    }

    suspend fun getProvidersSettings(): List<ProviderConfigDto> {
        return try {
            client.get("$BASE_URL/api/v1/settings/providers").body()
        } catch (_: Throwable) {
            emptyList()
        }
    }

    suspend fun saveApiKeys(
        glmApiKey: String?,
        geminiApiKey: String?,
        clearGlmKey: Boolean = false,
        clearGeminiKey: Boolean = false
    ): List<ProviderConfigDto> {
        return try {
            client.post("$BASE_URL/api/v1/settings/providers") {
                contentType(ContentType.Application.Json)
                setBody(
                    SaveKeysRequest(
                        glmApiKey = glmApiKey,
                        geminiApiKey = geminiApiKey,
                        clearGlmKey = clearGlmKey,
                        clearGeminiKey = clearGeminiKey
                    )
                )
            }.body()
        } catch (_: Throwable) {
            emptyList()
        }
    }

    suspend fun getFullSettings(): dev.danko.roblox_craft_ai.shared.model.UserSettingsDto? {
        return try {
            client.get("$BASE_URL/api/v1/settings/full").body()
        } catch (_: Throwable) {
            null
        }
    }

    suspend fun saveFullSettings(settings: dev.danko.roblox_craft_ai.shared.model.UserSettingsDto): dev.danko.roblox_craft_ai.shared.model.UserSettingsDto? {
        return try {
            client.post("$BASE_URL/api/v1/settings/full") {
                contentType(ContentType.Application.Json)
                setBody(settings)
            }.body()
        } catch (_: Throwable) {
            null
        }
    }

    suspend fun resetSettingsToDefaults(): dev.danko.roblox_craft_ai.shared.model.UserSettingsDto? {
        return try {
            client.post("$BASE_URL/api/v1/settings/reset").body()
        } catch (_: Throwable) {
            null
        }
    }

    suspend fun testApiKey(providerId: String, apiKey: String): TestKeyResponse {
        return try {
            client.post("$BASE_URL/api/v1/settings/test-key") {
                contentType(ContentType.Application.Json)
                setBody(TestKeyRequest(providerId = providerId, apiKey = apiKey))
            }.body()
        } catch (t: Throwable) {
            TestKeyResponse(
                valid = false,
                message = "Network error: ${t.message ?: "Failed to reach backend"}",
                providerId = providerId,
                latencyMs = 0L
            )
        }
    }

    /** Called when the backend answers AUTH_REQUIRED, so the UI can open the sign-in dialog. */
    var onAuthRequired: (() -> Unit)? = null

    /** Called after an action that may have spent credits, so the credits badge can refresh. */
    var onCreditsChanged: (() -> Unit)? = null

    suspend fun getBillingCatalog(): BillingCatalogDto? = try {
        val response = client.get("$BASE_URL/api/v1/billing/catalog")
        if (response.status.isSuccess()) response.body() else null
    } catch (_: Throwable) {
        null
    }

    suspend fun getBillingStatus(): BillingStatusDto? = try {
        val response = client.get("$BASE_URL/api/v1/billing/me")
        if (response.status.isSuccess()) response.body() else null
    } catch (_: Throwable) {
        null
    }

    suspend fun getUsage(): UsageSummaryDto? = try {
        val response = client.get("$BASE_URL/api/v1/usage/me")
        if (response.status.isSuccess()) response.body() else null
    } catch (_: Throwable) {
        null
    }

    private suspend inline fun <reified T> HttpResponse.bodyOrThrow(): T {
        if (status.isSuccess()) return body<T>().also { onCreditsChanged?.invoke() }
        throw apiException(status.value, runCatching { bodyAsText() }.getOrDefault(""))
    }

    private fun apiException(status: Int, text: String): ApiException {
        val parsed = runCatching { json.decodeFromString(ErrorResponse.serializer(), text) }.getOrNull()
        val code = parsed?.error_code ?: "HTTP_$status"
        if (code == "AUTH_REQUIRED") onAuthRequired?.invoke()
        return ApiException(code, parsed?.message ?: text.take(200))
    }

    /** Returns null on success, otherwise the backend error code. */
    suspend fun requestMagicLink(email: String, locale: String): String? = try {
        val response = client.post("$BASE_URL/api/v1/auth/magic-link") {
            contentType(ContentType.Application.Json)
            setBody(MagicLinkRequest(email = email, locale = locale))
        }
        if (response.status.isSuccess()) null else apiException(response.status.value, response.bodyAsText()).errorCode
    } catch (t: Throwable) {
        "NETWORK_ERROR"
    }

    /** Sends a request to join the invite-only beta; returns null on success or the server's error code. */
    suspend fun requestAccess(request: dev.danko.roblox_craft_ai.shared.model.AccessRequestDto): String? = try {
        val response = client.post("$BASE_URL/api/v1/access/request") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }
        if (response.status.isSuccess()) null else apiException(response.status.value, response.bodyAsText()).errorCode
    } catch (t: Throwable) {
        "NETWORK_ERROR"
    }

    /** Deletes the signed-in account; returns null on success or the server's error code. */
    suspend fun deleteAccount(confirmEmail: String): String? = try {
        val response = client.post("$BASE_URL/api/v1/account/delete") {
            contentType(ContentType.Application.Json)
            setBody(dev.danko.roblox_craft_ai.shared.model.AccountDeleteRequest(confirmEmail))
        }
        if (response.status.isSuccess()) null else apiException(response.status.value, response.bodyAsText()).errorCode
    } catch (t: Throwable) {
        "NETWORK_ERROR"
    }

    suspend fun verifyMagicLink(token: String): AuthUserDto =
        client.post("$BASE_URL/api/v1/auth/verify") {
            contentType(ContentType.Application.Json)
            setBody(VerifyMagicLinkRequest(token = token))
        }.bodyOrThrow()

    suspend fun currentUser(): AuthUserDto? = try {
        val response = client.get("$BASE_URL/api/v1/auth/me")
        if (response.status.isSuccess()) response.body() else null
    } catch (_: Throwable) {
        null
    }

    suspend fun logout(): Boolean = try {
        client.post("$BASE_URL/api/v1/auth/logout").status.isSuccess()
    } catch (_: Throwable) {
        false
    }

    suspend fun generate(request: AssetRequest): AssetResponse {
        return client.post("$BASE_URL/api/v1/generate") {
            headers { append("X-Session-Token", getSessionToken()) }
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrThrow()
    }

    suspend fun generateConcept(request: ConceptGenerationRequest): ConceptGenerationResult {
        return client.post("$BASE_URL/api/v1/pipeline/concept") {
            headers { append("X-Session-Token", getSessionToken()) }
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrThrow()
    }

    suspend fun reportAsset(assetId: String, reason: ReportReason, comment: String?): Boolean {
        return try {
            client.post("$BASE_URL/api/v1/assets/$assetId/report") {
                headers { append("X-Session-Token", getSessionToken()) }
                contentType(ContentType.Application.Json)
                setBody(AssetReportRequest(reason = reason, comment = comment))
            }.status.isSuccess()
        } catch (_: Throwable) {
            false
        }
    }

    suspend fun getConceptStatus(id: String): ConceptGenerationResult {
        return client.get("$BASE_URL/api/v1/pipeline/concept/$id").body()
    }

    suspend fun approveConcept(request: ApproveConceptRequest): AssetResponse {
        return client.post("$BASE_URL/api/v1/pipeline/concept/${request.conceptId}/approve") {
            headers { append("X-Session-Token", getSessionToken()) }
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrThrow()
    }

    suspend fun rerollConcept(request: RerollConceptRequest): ConceptGenerationResult {
        return client.post("$BASE_URL/api/v1/pipeline/concept/${request.conceptId}/reroll") {
            headers { append("X-Session-Token", getSessionToken()) }
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrThrow()
    }

    suspend fun getStatus(id: String): AssetResponse {
        return client.get("$BASE_URL/api/v1/status/$id").body()
    }

    suspend fun cancelGeneration(id: String): Boolean {
        return try {
            val response = awaitDynamic(
                window.asDynamic().fetch(
                    "$BASE_URL/api/v1/assets/$id/cancel",
                    js("({ method: 'POST', headers: { 'Accept': 'application/json' } })")
                )
            )
            response.status == 200
        } catch (_: Throwable) {
            false
        }
    }

    suspend fun deleteAsset(id: String): Boolean {
        return try {
            val response = awaitDynamic(
                window.asDynamic().fetch(
                    "$BASE_URL/api/v1/assets/$id",
                    js("({ method: 'DELETE', headers: { 'Accept': 'application/json' } })")
                )
            )
            response.status == 200
        } catch (_: Throwable) {
            false
        }
    }

    private fun enc(v: String): String = js("encodeURIComponent(v)") as String

    suspend fun syncRoblox(token: String): SessionSync {
        return client.get("$BASE_URL/api/v1/roblox/sync/${enc(token)}").body()
    }

    suspend fun markAssetForSync(token: String, assetId: String): Boolean {
        return try {
            val response = client.post("$BASE_URL/api/v1/roblox/sync/${enc(token)}/${enc(assetId)}") {
                contentType(ContentType.Application.Json)
            }
            response.status.value in 200..299
        } catch (_: Throwable) {
            false
        }
    }

    suspend fun getSyncStatus(token: String): Map<String, String> {
        return try {
            val encodedToken = enc(token)
            val response = awaitDynamic(
                window.asDynamic().fetch(
                    "$BASE_URL/api/v1/sync-status/$encodedToken",
                    js("({ method: 'GET', headers: { 'Accept': 'application/json' } })")
                )
            )
            if (response.status == 200) {
                val jsonText = awaitDynamic(response.text()) as String
                val element = json.parseToJsonElement(jsonText).jsonObject
                element.mapValues { it.value.jsonPrimitive.content }
            } else {
                emptyMap()
            }
        } catch (_: Throwable) {
            emptyMap()
        }
    }

    fun pollStatusFlow(id: String, intervalMs: Long = 2000): Flow<AssetResponse> = flow {
        var latest = getStatus(id)
        emit(latest)
        while (latest.status !in setOf(
                dev.danko.roblox_craft_ai.shared.model.GenerationStatus.READY,
                dev.danko.roblox_craft_ai.shared.model.GenerationStatus.FAILED
            )
        ) {
            delay(intervalMs)
            latest = getStatus(id)
            emit(latest)
        }
    }

    private suspend fun awaitDynamic(promise: dynamic): dynamic = suspendCancellableCoroutine { cont ->
        promise.then(
            { res: dynamic -> cont.resume(res) },
            { err: dynamic -> cont.resumeWithException(Exception(err.toString())) }
        )
    }

    suspend fun streamGeneration(
        id: String,
        onToken: (chunk: String, n: Int) -> Unit,
        onStatus: (s: String) -> Unit,
        onDone: (valid: Boolean, tokens: Int) -> Unit
    ) {
        try {
            val response = awaitDynamic(window.asDynamic().fetch("$BASE_URL/api/v1/stream/$id"))
            val reader = response.body.getReader()
            val decoder = js("new TextDecoder()")
            var buffer = ""

            while (true) {
                val chunkResult = awaitDynamic(reader.read())
                val done = chunkResult.done as? Boolean ?: false
                val value = chunkResult.value
                if (value != null && value != undefined) {
                    val decoded = decoder.decode(value, js("({stream: true})")) as String
                    buffer += decoded
                    val blocks = buffer.split("\n\n")
                    buffer = blocks.last()
                    for (i in 0 until blocks.size - 1) {
                        parseSseEventBlock(blocks[i], onToken, onStatus, onDone)
                    }
                }
                if (done) break
            }
            if (buffer.isNotBlank()) {
                parseSseEventBlock(buffer, onToken, onStatus, onDone)
            }
        } catch (_: Throwable) {
        }
    }

    private fun parseSseEventBlock(
        block: String,
        onToken: (chunk: String, n: Int) -> Unit,
        onStatus: (s: String) -> Unit,
        onDone: (valid: Boolean, tokens: Int) -> Unit
    ) {
        var eventType = "message"
        var dataContent = ""
        val lines = block.lines()
        for (line in lines) {
            if (line.startsWith("event:")) {
                eventType = line.removePrefix("event:").trim()
            } else if (line.startsWith("data:")) {
                dataContent = line.removePrefix("data:").trim()
            }
        }
        if (dataContent.isBlank()) return

        try {
            val element = json.parseToJsonElement(dataContent).jsonObject
            when (eventType) {
                "token" -> {
                    val chunk = element["t"]?.jsonPrimitive?.content ?: ""
                    val n = element["n"]?.jsonPrimitive?.intOrNull ?: 0
                    if (chunk.isNotEmpty()) {
                        onToken(chunk, n)
                    }
                }
                "status" -> {
                    val s = element["s"]?.jsonPrimitive?.content ?: ""
                    if (s.isNotEmpty()) {
                        onStatus(s)
                    }
                }
                "done" -> {
                    val valid = element["finalValid"]?.jsonPrimitive?.booleanOrNull ?: true
                    val tokens = element["tokens"]?.jsonPrimitive?.intOrNull ?: 0
                    onDone(valid, tokens)
                }
            }
        } catch (_: Throwable) {
        }
    }

    suspend fun refactorAsset(
        assetId: String,
        instruction: String,
        activeFilePath: String? = null,
        selectedCodeSnippet: String? = null
    ): dev.danko.roblox_craft_ai.shared.model.RefactorResponse {
        val req = dev.danko.roblox_craft_ai.shared.model.RefactorRequest(
            assetId = assetId,
            instruction = instruction,
            activeFilePath = activeFilePath,
            selectedCodeSnippet = selectedCodeSnippet
        )
        val body = json.encodeToString(dev.danko.roblox_craft_ai.shared.model.RefactorRequest.serializer(), req)
        @Suppress("UNUSED_VARIABLE")
        val sessionToken = getSessionToken()
        val response = awaitDynamic(
            window.asDynamic().fetch(
                "$BASE_URL/api/v1/assets/$assetId/refactor",
                js("({ method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Session-Token': sessionToken }, body: body })")
            )
        )
        val text = awaitDynamic(response.text()) as String
        if (response.ok != true) throw apiException((response.status as? Int) ?: 0, text)
        onCreditsChanged?.invoke()
        return json.decodeFromString(dev.danko.roblox_craft_ai.shared.model.RefactorResponse.serializer(), text)
    }

    suspend fun getChatHistory(assetId: String): List<dev.danko.roblox_craft_ai.shared.model.ChatMessage> {
        val response = awaitDynamic(
            window.asDynamic().fetch(
                "$BASE_URL/api/v1/assets/$assetId/chat",
                js("({ method: 'GET', headers: { 'Accept': 'application/json' } })")
            )
        )
        val text = awaitDynamic(response.text()) as String
        return json.decodeFromString(
            kotlinx.serialization.builtins.ListSerializer(dev.danko.roblox_craft_ai.shared.model.ChatMessage.serializer()),
            text
        )
    }

    suspend fun applyRefactoredMessage(
        assetId: String,
        messageId: String
    ): dev.danko.roblox_craft_ai.shared.model.AssetResponse {
        val response = awaitDynamic(
            window.asDynamic().fetch(
                "$BASE_URL/api/v1/assets/$assetId/apply-message/$messageId",
                js("({ method: 'POST', headers: { 'Accept': 'application/json' } })")
            )
        )
        val text = awaitDynamic(response.text()) as String
        return json.decodeFromString(dev.danko.roblox_craft_ai.shared.model.AssetResponse.serializer(), text)
    }

    suspend fun getMeshInfo(assetId: String): dev.danko.roblox_craft_ai.shared.model.MeshInfo? = try {
        client.get("$BASE_URL/api/v1/assets/$assetId/mesh-info").bodyOrThrow()
    } catch (_: Throwable) {
        null
    }

    suspend fun editMesh(assetId: String, request: dev.danko.roblox_craft_ai.shared.model.MeshEditRequest): AssetResponse =
        client.post("$BASE_URL/api/v1/assets/$assetId/mesh-edit") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrThrow()

    fun meshObjZipUrl(assetId: String): String = "$BASE_URL/api/v1/assets/$assetId/model-obj.zip"
    fun meshSharpGlbUrl(assetId: String): String = "$BASE_URL/api/v1/assets/$assetId/model-2k.glb"

    suspend fun rigMesh(assetId: String): AssetResponse =
        client.post("$BASE_URL/api/v1/assets/$assetId/rig").bodyOrThrow()

    /** The user's own model files as a new 3D work (fitted to Roblox on the server). */
    suspend fun importModel(files: List<org.w3c.files.File>, kind: dev.danko.roblox_craft_ai.shared.model.ModelImportKind, name: String? = null): AssetResponse {
        val parts = files.map { file ->
            val buffer = file.asDynamic().arrayBuffer().unsafeCast<kotlin.js.Promise<org.khronos.webgl.ArrayBuffer>>().await()
            file.name to org.khronos.webgl.Int8Array(buffer).unsafeCast<ByteArray>()
        }
        return client.submitFormWithBinaryData("$BASE_URL/api/v1/models/import", io.ktor.client.request.forms.formData {
            append("kind", kind.name)
            if (!name.isNullOrBlank()) append("name", name)
            parts.forEach { (fileName, bytes) ->
                append("files", bytes, Headers.build {
                    append(HttpHeaders.ContentDisposition, "filename=\"${fileName.replace("\"", "")}\"")
                    append(HttpHeaders.ContentType, "application/octet-stream")
                })
            }
        }).bodyOrThrow()
    }

    /** A picture of the work drawn in the browser (PNG data URL). */
    suspend fun setPreview(assetId: String, dataUrl: String): AssetResponse =
        client.put("$BASE_URL/api/v1/assets/$assetId/preview") {
            contentType(ContentType.Application.Json)
            setBody(dev.danko.roblox_craft_ai.shared.model.ModelPreviewRequest(dataUrl))
        }.bodyOrThrow()

    suspend fun renameAsset(assetId: String, title: String): AssetResponse =
        client.put("$BASE_URL/api/v1/assets/$assetId/title") {
            contentType(ContentType.Application.Json)
            setBody(dev.danko.roblox_craft_ai.shared.model.RenameAssetRequest(title))
        }.bodyOrThrow()

    suspend fun editAnimation(assetId: String, instruction: String): AssetResponse =
        client.post("$BASE_URL/api/v1/assets/$assetId/animation-edit") {
            contentType(ContentType.Application.Json)
            setBody(dev.danko.roblox_craft_ai.shared.model.AnimationEditRequest(instruction))
        }.bodyOrThrow()

    suspend fun restoreAnimation(assetId: String, revisionNumber: Int): AssetResponse =
        client.post("$BASE_URL/api/v1/assets/$assetId/animation-restore/$revisionNumber").bodyOrThrow()

    suspend fun rigMeshPro(assetId: String): AssetResponse =
        client.post("$BASE_URL/api/v1/assets/$assetId/rig-pro").bodyOrThrow()

    suspend fun retextureMesh(assetId: String, request: dev.danko.roblox_craft_ai.shared.model.RetextureRequest): AssetResponse =
        client.post("$BASE_URL/api/v1/assets/$assetId/retexture") {
            contentType(ContentType.Application.Json)
            setBody(request)
        }.bodyOrThrow()

    /** A model sheet cut into front/back/left/right pictures for the 3D view slots. */
    suspend fun splitViews(image: String): Map<String, String> =
        client.post("$BASE_URL/api/v1/mesh/split-views") {
            contentType(ContentType.Application.Json)
            setBody(dev.danko.roblox_craft_ai.shared.model.SplitViewsRequest(image))
        }.bodyOrThrow<dev.danko.roblox_craft_ai.shared.model.SplitViewsResponse>().views

    suspend fun rollbackRevision(
        assetId: String,
        targetRevisionNumber: Int
    ): dev.danko.roblox_craft_ai.shared.model.RollbackResponse {
        val response = awaitDynamic(
            window.asDynamic().fetch(
                "$BASE_URL/api/v1/assets/$assetId/revisions/$targetRevisionNumber/restore",
                js("({ method: 'POST', headers: { 'Accept': 'application/json' } })")
            )
        )
        val text = awaitDynamic(response.text()) as String
        return json.decodeFromString(dev.danko.roblox_craft_ai.shared.model.RollbackResponse.serializer(), text)
    }

    suspend fun isBackendReachable(): Boolean {
        return try {
            val controller: dynamic = js("new AbortController()")
            val timeoutId: Int = js("setTimeout(function() { controller.abort(); }, 3000)")
            try {
                val opts = js("({ method: 'GET', headers: { 'Accept': 'application/json' } })")
                opts.signal = controller.signal
                awaitDynamic(window.asDynamic().fetch("$BASE_URL/health", opts))
                true
            } finally {
                js("clearTimeout(timeoutId)")
            }
        } catch (_: Throwable) {
            false
        }
    }

    suspend fun getHealth(): HealthResponse? {
        return try {
            val controller: dynamic = js("new AbortController()")
            val signal = controller.signal
            val timeoutId: Int = js("setTimeout(function() { controller.abort(); }, 2000)")
            try {
                val url = "$BASE_URL/health"
                val opts = js("({ method: 'GET', headers: { 'Accept': 'application/json' } })")
                opts.signal = signal
                val response = awaitDynamic(window.asDynamic().fetch(url, opts))
                if (response.status == 200 || response.status == 503) {
                    val text = awaitDynamic(response.text()) as String
                    json.decodeFromString(HealthResponse.serializer(), text)
                } else {
                    null
                }
            } finally {
                try {
                    js("clearTimeout(timeoutId)")
                } catch (_: Throwable) {}
            }
        } catch (_: Throwable) {
            null
        }
    }

    fun getSessionToken(): String {
        return try {
            (window.asDynamic().localStorage.getItem("robloxCraft.sessionToken") as? String)?.takeIf { it.isNotBlank() }
                ?: run {
                    val fresh = "rc_${(0 until 32).joinToString("") {
                        "0123456789abcdef".random().toString()
                    }}"
                    window.asDynamic().localStorage.setItem("robloxCraft.sessionToken", fresh)
                    fresh
                }
        } catch (_: Throwable) {
            "rc_00000000000000000000000000000000"
        }
    }

    private fun buildV2Headers(): Map<String, String> {
        return mapOf(
            "X-Session-Token" to getSessionToken(),
            "Accept" to "application/json",
            "Content-Type" to "application/json"
        )
    }

    suspend fun v2GetProviderCatalog(): List<ProviderMetadataDto> {
        return try {
            client.get("$BASE_URL/api/v2/settings/provider-catalog") {
                headers { append("X-Session-Token", getSessionToken()) }
            }.body()
        } catch (_: Throwable) {
            emptyList()
        }
    }

    suspend fun v2GetUserKeys(): UserSettingsDtoV2? {
        return try {
            client.get("$BASE_URL/api/v2/settings/keys") {
                headers { append("X-Session-Token", getSessionToken()) }
            }.body()
        } catch (_: Throwable) {
            null
        }
    }

    suspend fun v2CreateKey(
        providerId: String,
        label: String,
        apiKey: String,
        baseUrl: String? = null
    ): ProviderKeyDto? {
        return try {
            client.post("$BASE_URL/api/v2/settings/keys") {
                headers { append("X-Session-Token", getSessionToken()) }
                contentType(ContentType.Application.Json)
                setBody(
                    UpsertProviderKeyRequest(
                        providerId = providerId,
                        label = label,
                        plaintextKey = apiKey,
                        baseUrl = baseUrl
                    )
                )
            }.body()
        } catch (_: Throwable) {
            null
        }
    }

    suspend fun v2UpdateKey(
        keyId: String,
        label: String? = null,
        baseUrl: String? = null,
        isEnabled: Boolean? = null,
        priority: Int? = null,
        newPlaintextKey: String? = null
    ): ProviderKeyDto? {
        return try {
            client.patch("$BASE_URL/api/v2/settings/keys/$keyId") {
                headers { append("X-Session-Token", getSessionToken()) }
                contentType(ContentType.Application.Json)
                setBody(
                    UpsertProviderKeyRequest(
                        providerId = "",
                        label = label ?: "Updated",
                        baseUrl = baseUrl,
                        isEnabled = isEnabled ?: true,
                        priority = priority,
                        plaintextKey = newPlaintextKey
                    )
                )
            }.body()
        } catch (_: Throwable) {
            null
        }
    }

    suspend fun v2DeleteKey(keyId: String): Boolean {
        return try {
            val resp = client.delete("$BASE_URL/api/v2/settings/keys/$keyId") {
                headers { append("X-Session-Token", getSessionToken()) }
            }
            resp.status.value in 200..299
        } catch (_: Throwable) {
            false
        }
    }

    suspend fun v2ReorderKeys(orderedKeyIds: List<String>): Boolean {
        return try {
            val resp = client.post("$BASE_URL/api/v2/settings/keys/reorder") {
                headers { append("X-Session-Token", getSessionToken()) }
                contentType(ContentType.Application.Json)
                setBody(ReorderKeysRequest(orderedKeyIds = orderedKeyIds))
            }
            resp.status.value in 200..299
        } catch (_: Throwable) {
            false
        }
    }

    suspend fun v2TestSavedKey(keyId: String): TestKeyResponseV2 {
        return try {
            client.post("$BASE_URL/api/v2/settings/keys/$keyId/test") {
                headers { append("X-Session-Token", getSessionToken()) }
            }.body()
        } catch (_: Throwable) {
            TestKeyResponseV2(
                message = "Network error: failed to reach backend"
            )
        }
    }

    suspend fun v2TestAdhocKey(
        providerId: String,
        plaintextApiKey: String,
        baseUrl: String? = null
    ): TestKeyResponseV2 {
        return try {
            client.post("$BASE_URL/api/v2/settings/keys/test") {
                headers { append("X-Session-Token", getSessionToken()) }
                contentType(ContentType.Application.Json)
                setBody(
                    AdhocTestKeyRequest(
                        providerId = providerId,
                        apiKeyPlaintext = plaintextApiKey,
                        baseUrl = baseUrl
                    )
                )
            }.body()
        } catch (_: Throwable) {
            TestKeyResponseV2(
                message = "Network error: failed to reach backend"
            )
        }
    }

    suspend fun v2GetPreferences(): Map<String, String> {
        return try {
            client.get("$BASE_URL/api/v2/settings/preferences") {
                headers { append("X-Session-Token", getSessionToken()) }
            }.body()
        } catch (_: Throwable) {
            emptyMap()
        }
    }

    suspend fun v2PatchPreferences(
        defaultModelId: String? = null,
        defaultAssetType: String? = null,
        skipSyncConfirmations: Boolean? = null,
        themeMode: String? = null,
        userLocale: String? = null,
        pluginPort: Int? = null,
        pluginAutoConnect: Boolean? = null,
        robloxStudioInstallPath: String? = null
    ): Map<String, String> {
        return try {
            client.patch("$BASE_URL/api/v2/settings/preferences") {
                headers { append("X-Session-Token", getSessionToken()) }
                contentType(ContentType.Application.Json)
                setBody(
                    UpdatePreferencesRequest(
                        defaultModelId = defaultModelId,
                        skipSyncConfirmations = skipSyncConfirmations,
                        themeMode = themeMode,
                        userLocale = userLocale,
                        pluginPort = pluginPort,
                        pluginAutoConnect = pluginAutoConnect,
                        robloxStudioInstallPath = robloxStudioInstallPath
                    )
                )
            }.body()
        } catch (_: Throwable) {
            emptyMap()
        }
    }

    suspend fun v2GetFullSettings(): FullSettingsResponse? {
        return try {
            client.get("$BASE_URL/api/v2/settings/full") {
                headers { append("X-Session-Token", getSessionToken()) }
            }.body()
        } catch (_: Throwable) {
            null
        }
    }

    suspend fun v2DeleteSession(): Boolean {
        return try {
            val resp = client.delete("$BASE_URL/api/v2/settings/session") {
                headers { append("X-Session-Token", getSessionToken()) }
            }
            resp.status.value in 200..299
        } catch (_: Throwable) {
            false
        }
    }

    suspend fun v2GenerateWithUserKeys(request: AssetRequest): AssetResponse {
        return client.post("$BASE_URL/api/v1/generate") {
            headers { append("X-Session-Token", getSessionToken()) }
            contentType(ContentType.Application.Json)
            setBody(request)
        }.body()
    }
}
